import React, { useState } from 'react'
import Header from '../../components/Header'
// import { supabase } from '../../supabaseClient'
import { useNavigate } from 'react-router-dom'
import { useCustomerFeedbackAIHistory } from '../../hooks/useCustomerFeedbackAIHistory'
import { useCustomerFeedbackData } from '../../hooks/useCustomerFeedbackData'
import { authHeaders } from '../../utils/authFetch'

function timeAgo (dateStr) {
    const diff = (Date.now() -  new Date(dateStr)) / 1000
    if (diff < 60) return 'just now'
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
    if (diff < 6040800) return `${Math.floor(diff / 86400)}d ago`
    return new Date(dateStr).toLocaleDateString('en-ph', { month: 'short', day: 'numeric'})

}

function getInitials(userId) {
    return userId ? userId.slice(0, 2).toUpperCase() : '??'
}

const AVATAR_COLORS = [
    'bg-blue-500', 
    'bg-purple-500', 
    'bg-green-500',
    'bg-rose-500',
    'bg-amber-500',
    'bg-teal-500'
]
const CARD = "bg-white rounded-2xl border border-gray-200 shadow-[0_1px_4px_rgba(0,0,0,0.05)]"

const C = {
    espresso: 'transparent',
    dark: '#F9FAFB',
    card: '#FFFFFF',
    border: '#E5E7EB',
    muted: '#9CA3AF',
    text: '#111827',
    textSoft: '#6B7280',
    amber: '#D97706',
    gold: '#B45309',
    teal: '#0D9488',
    coral: '#DC2626',
    blue: '#2563EB',
    green: '#16A34A',
    purple: '#7C3AED',
    indigo: '#4F46E5',
    nude: '#A0826D',
    midgrey: '#6B7280',
}

function avatarColor(userId) {
    const idx = userId ? userId.charCodeAt(0) % AVATAR_COLORS.length : 0
    return AVATAR_COLORS[idx]
}

function StarRow({rating, max = 5}) {
    return (
        <div className='flex items-center'>
            {Array.from({length: max}).map((_, i) => (
                 <svg key={i} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" className={i < rating ? 'text-yellow-400' : 'text-gray-200'}><path fill="currentColor" d="m12 17.27l4.15 2.51c.76.46 1.69-.22 1.49-1.08l-1.1-4.72l3.67-3.18c.67-.58.31-1.68-.57-1.75l-4.83-.41l-1.89-4.46c-.34-.81-1.5-.81-1.84 0L9.19 8.63l-4.83.41c-.88.07-1.24 1.17-.57 1.75l3.67 3.18l-1.1 4.72c-.2.86.73 1.54 1.49 1.08z" /></svg>
            ))}
        </div>
    )
}

const REVIEW_PER_PAGE = 5
const FILTER_OPTIONS = ['all', '5', '4', '3', '2', '1']

function Pagination({ page, totalItems, pageSize, onPageChange }) {
    const totalPages = Math.ceil(totalItems / pageSize)
    if (totalPages <= 1) return null

    return (
        <div className="flex items-center justify-between px-4 py-2 rounded-b-xl">
            <span style={{ fontSize: 11, color: C.muted }}>
                {Math.min((page - 1) * pageSize + 1, totalItems)}–{Math.min(page * pageSize, totalItems)} of {totalItems} items
            </span>
            <div className="flex items-center gap-1">
                <button
                    onClick={() => onPageChange(Math.max(1, page - 1))}
                    disabled={page === 1}
                    style={{
                        padding: "3px 10px", borderRadius: 8,
                        border: `1px solid ${C.border}`, background: C.card,
                        color: page === 1 ? C.border : C.textSoft,
                        fontSize: 11, cursor: page === 1 ? "not-allowed" : "pointer",
                    }}>
                    ← Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                    .reduce((acc, n, idx, arr) => {
                        if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
                        acc.push(n)
                        return acc
                    }, [])
                    .map((n, idx) =>
                        n === "…" ? (
                            <span key={`e-${idx}`} style={{ fontSize: 11, color: C.muted, padding: "0 2px" }}>…</span>
                        ) : (
                            <button key={n} onClick={() => onPageChange(n)}
                                style={{
                                    width: 28, height: 28, borderRadius: 8,
                                    border: `1px solid ${n === page ? C.nude : C.border}`,
                                    background: n === page ? C.nude : C.card,
                                    color: n === page ? "#fff" : C.textSoft,
                                    fontSize: 11, fontWeight: n === page ? 600 : 400,
                                    cursor: "pointer",
                                }}>
                                {n}
                            </button>
                        )
                    )
                }
                <button
                    onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    style={{
                        padding: "3px 10px", borderRadius: 8,
                        border: `1px solid ${C.border}`, background: C.card,
                        color: page === totalPages ? C.border : C.textSoft,
                        fontSize: 11, cursor: page === totalPages ? "not-allowed" : "pointer",
                    }}>
                    Next →
                </button>
            </div>
        </div>
    )
}

const parseAISections = (text) => {
  if (!text) return []
  const lines = text.split('\n')
  const sections = []
  let current = null
 
  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed) continue
 
    const isHeader = trimmed.startsWith('### ')
    if (isHeader) {
      if (current) sections.push(current)
      current = { title: trimmed.replace(/^###\s*/, ''), bullets: [] }
    } else if (/^[-•*]\s+/.test(trimmed)) {
      if (!current) current = { title: null, bullets: [] }
      current.bullets.push(trimmed.replace(/^[-•*]\s+/, ''))
    } else {
      if (!current) current = { title: null, bullets: [] }
      current.bullets.push(trimmed)
    }
  }
  if (current) sections.push(current)
  return sections
}

const LOYALTY_SECTION_COLORS = {
  'TIER DISTRIBUTION': { color: C.blue, icon: '🏆' },
  'REDEMPTION ACTIVITY': { color: C.purple, icon: '🎁' },
  'REWARD PERFORMANCE': { color: C.teal, icon: '📈' },
  'MEMBER GROWTH': { color: C.green, icon: '👥' },
  'AT-RISK / STALLED TIERS':{ color: C.coral,  icon: '⚠️' },
}
const DEFAULT_SECTION_COLORS = [C.purple, C.teal, C.blue, C.green, C.coral, C.amber]
 
const renderBulletText = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i} style={{ fontWeight: 600, color: C.text }}>{part.slice(2, -2)}</strong>
      : part
  )
}
 
const MarkdownBlock = ({ text }) => {
  if (!text) return null
  const sections = parseAISections(text)
  if (!sections.length) return null
 
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {sections.map((section, idx) => {
        const key = section.title?.toUpperCase().trim()
        const cfg = (key && LOYALTY_SECTION_COLORS[key]) || {
          color: DEFAULT_SECTION_COLORS[idx % DEFAULT_SECTION_COLORS.length],
          icon: '•',
        }
        return (
          <div key={idx} style={{ borderRadius: 10, border: `1px solid ${cfg.color}28`, overflow: 'hidden' }}>
            {section.title && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                background: cfg.color + '10', borderBottom: `1px solid ${cfg.color}20`,
              }}>
                <span style={{ fontSize: 13 }}>{cfg.icon}</span>
                <span style={{
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '0.07em', color: cfg.color,
                }}>{section.title}</span>
              </div>
            )}
            <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 5 }}>
              {section.bullets.map((bullet, bIdx) => (
                <div key={bIdx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.color, flexShrink: 0, marginTop: 7 }} />
                  <span style={{ fontSize: 13, lineHeight: 1.65, color: C.text }}>{renderBulletText(bullet)}</span>
                </div>
              ))}
              {section.bullets.length === 0 && (
                <span style={{ fontSize: 13, lineHeight: 1.65, color: C.text }}>{section.title}</span>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
 
//Sidebar 
const FeedbackAISidebar = ({
  open, onClose, summary, recommendations, loading, error, onGenerate,
  hasData, history, historyLoading, onDeleteHistory,
}) => {
  const [activeTab, setActiveTab] = useState("analysis") // "analysis" | "history"
  const [expandedId, setExpandedId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
 
  const handleDelete = async (id) => {
    setDeletingId(id)
    await onDeleteHistory(id)
    if (expandedId === id) setExpandedId(null)
    setDeletingId(null)
  }
 
  return (
    <>
      {open && (
        <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.18)", zIndex: 999 }} />
      )}
 
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(420px, 100vw)",
        background: "#fff", borderLeft: `1px solid ${C.border}`, zIndex: 1000,
        display: "flex", flexDirection: "column",
        transform: open ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
        boxShadow: open ? "-4px 0 24px rgba(0,0,0,0.10)" : "none",
      }}>
        {/* Header */}
        <div style={{
          padding: "16px 20px", borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0, background: C.purple + "06",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: "50%", background: C.purple,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24">
                <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
              </svg>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>Customer Feedback Analyst</p>
              <p style={{ margin: 0, fontSize: 11, color: C.muted }}>Ratings · Sentiment · Tags</p>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer", padding: 6,
            borderRadius: 8, color: C.muted, display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
 
        {/* Tab switcher */}
        <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: C.dark }}>
          {[
            { id: "analysis", label: "Analysis" },
            { id: "history", label: `History${history?.length ? ` (${history.length})` : ""}` },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              flex: 1, padding: "10px 0", background: "none", border: "none",
              borderBottom: activeTab === tab.id ? `2px solid ${C.purple}` : "2px solid transparent",
              color: activeTab === tab.id ? C.purple : C.muted,
              fontSize: 12, fontWeight: activeTab === tab.id ? 700 : 400,
              cursor: "pointer", transition: "all 0.15s", marginBottom: -1,
            }}>{tab.label}</button>
          ))}
        </div>
 
        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px", minHeight: 0 }}>
          {activeTab === "analysis" && (
            <>
              <button
                onClick={onGenerate}
                disabled={loading || !hasData}
                style={{
                  width: "100%", padding: "10px 16px", borderRadius: 50, border: "none",
                  background: loading || !hasData ? C.border : C.purple, color: "#fff",
                  fontSize: 12, fontWeight: 600, cursor: loading || !hasData ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                  marginBottom: summary ? 8 : 20, transition: "background 0.2s",
                }}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 14, height: 14 }}>
                      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="4" />
                      <path fill="#fff" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Analyzing customer feedback data…
                  </>
                ) : summary ? (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                      <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.62"/>
                    </svg>
                    Regenerate Analysis
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                      <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
                    </svg>
                    Generate Analysis
                  </>
                )}
              </button>
 
              {summary && !loading && (
                <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
                  {["🤖 Gemini 2.5 Flash", "💬 Based on your customer feedback data", "⚠️ For reference only"].map(label => (
                    <span key={label} style={{
                      fontSize: 9, padding: "2px 8px", borderRadius: 50,
                      border: `1px solid ${C.border}`, color: C.muted, background: C.dark,
                    }}>{label}</span>
                  ))}
                </div>
              )}
 
              <div style={{
                padding: "10px 14px", borderRadius: 10, background: C.dark, border: `1px solid ${C.border}`,
                display: "flex", gap: 8, alignItems: "flex-start",
              }}>
                <span style={{ fontSize: 13, flexShrink: 0 }}>⚠️</span>
                <p style={{ margin: 0, fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
                  <strong style={{ color: C.textSoft }}>AI-Generated Content.</strong>{" "}
                  This analysis is produced by Google Gemini 2.5 Flash based on your customer feedback data. Insights are pattern-based interpretations, not verified findings.
                  Validate against your own business judgment before making decisions.
                </p>
              </div>
 
              {error && (
                <div style={{
                  background: C.coral + "15", border: `1px solid ${C.coral}44`, borderRadius: 10,
                  padding: "10px 14px", fontSize: 12, color: C.coral, marginTop: 16,
                }}>{error}</div>
              )}
 
              {!summary && !loading && !error && (
                <div style={{
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  gap: 12, padding: "40px 20px", textAlign: "center",
                }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: "50%", background: C.purple + "12",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24">
                      <path fill={C.purple} opacity="0.7" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
                    </svg>
                  </div>
                  <div>
                    <p style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 600, color: C.text }}>
                      Customer Feedback Interpretation
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: C.textSoft, maxWidth: 260, lineHeight: 1.6 }}>
                      Click <strong>Generate Analysis</strong> to get a plain-language reading of tier health, redemption behavior, and reward ROI.
                    </p>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%", marginTop: 4 }}>
                    {[
                      { icon: "⭐", label: "Rating distribution" },
                      { icon: "💬", label: "Common feedback tags" },
                      { icon: "📈", label: "Sentiment trend" },
                      { icon: "⚠️", label: "Low-rated / at-risk reviews" },
                    ].map(item => (
                      <div key={item.label} style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                        borderRadius: 8, background: C.dark, border: `1px solid ${C.border}`,
                      }}>
                        <span style={{ fontSize: 14 }}>{item.icon}</span>
                        <span style={{ fontSize: 12, color: C.textSoft }}>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
 
              {loading && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "40px 20px" }}>
                  <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 28, height: 28 }}>
                    <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
                    <path fill={C.purple} d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  <p style={{ margin: 0, fontSize: 12, color: C.muted }}>Reading your customer feedback data…</p>
                </div>
              )}
 
              {summary && !loading && (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div style={{ background: C.purple + "08", border: `1px solid ${C.purple}28`, borderRadius: 12, overflow: "hidden" }}>
                    <div style={{
                      padding: "10px 14px", borderBottom: `1px solid ${C.purple}18`,
                      background: C.purple + "10", display: "flex", alignItems: "center", gap: 8,
                    }}>
                      <span style={{ fontSize: 14 }}>💬</span>
                      <p style={{ margin: 0, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: C.purple }}>
                        What the Data Shows
                      </p>
                    </div>
                    <div style={{ padding: "12px 14px" }}><MarkdownBlock text={summary} /></div>
                  </div>
 
                  {recommendations && (
                    <div style={{ background: C.teal + "08", border: `1px solid ${C.teal}28`, borderRadius: 12, overflow: "hidden" }}>
                      <div style={{
                        padding: "10px 14px", borderBottom: `1px solid ${C.teal}18`,
                        background: C.teal + "10", display: "flex", alignItems: "center", gap: 8,
                      }}>
                        <span style={{ fontSize: 14 }}>🔮</span>
                        <p style={{ margin: 0, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: C.teal }}>
                          Outlook & What to Anticipate
                        </p>
                      </div>
                      <div style={{ padding: "12px 14px" }}><MarkdownBlock text={recommendations} /></div>
                    </div>
                  )}
 
                  <div style={{
                    padding: "10px 14px", borderRadius: 10, background: C.amber + "08",
                    border: `1px solid ${C.amber}22`, display: "flex", gap: 8, alignItems: "flex-start",
                  }}>
                    <span style={{ fontSize: 13, flexShrink: 0 }}>ℹ️</span>
                    <p style={{ margin: 0, fontSize: 11, color: C.textSoft, lineHeight: 1.6 }}>
                      This analysis reflects the filters active when it was generated. If you've changed the date range, click <strong>Regenerate</strong> for a fresh read.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
 
          {activeTab === "history" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {historyLoading ? (
                <div className="flex items-center justify-center gap-2" style={{ height: 160 }}>
                  <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
                    <path fill={C.amber} d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                </div>
              ) : !history?.length ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "40px 20px", textAlign: "center" }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: "50%", background: C.muted + "18",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
                  }}>🕓</div>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.text }}>No history yet</p>
                  <p style={{ margin: 0, fontSize: 12, color: C.textSoft, lineHeight: 1.6 }}>
                    Generated analyses will be saved here automatically.
                  </p>
                </div>
              ) : history.map((entry) => {
                const isOpen = expandedId === entry.id
                const generatedDate = new Date(entry.generated_at)
                return (
                  <div key={entry.id} style={{
                    borderRadius: 12, border: `1px solid ${isOpen ? C.purple + "44" : C.border}`,
                    overflow: "hidden", transition: "border-color 0.15s",
                  }}>
                    <div
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "10px 14px", background: isOpen ? C.purple + "08" : C.dark,
                        cursor: "pointer", gap: 10, transition: "background 0.15s",
                      }}
                      onClick={() => setExpandedId(isOpen ? null : entry.id)}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span style={{
                            fontSize: 10, fontWeight: 700, color: C.purple,
                            background: C.purple + "15", borderRadius: 50, padding: "2px 8px",
                          }}>
                            {entry.branch_name || "Customer Feedback"}
                          </span>
                          <span style={{
                            fontSize: 10, color: C.textSoft, background: C.border + "80",
                            borderRadius: 50, padding: "2px 8px",
                          }}>
                            {entry.date_range || "All Time"}
                          </span>
                        </div>
                        <p style={{ margin: "5px 0 0", fontSize: 11, color: C.muted }}>
                          {generatedDate.toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" })}
                          {" at "}
                          {generatedDate.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(entry.id) }}
                          disabled={deletingId === entry.id}
                          title="Delete this entry"
                          style={{
                            background: "none", border: `1px solid ${C.border}`, borderRadius: 6,
                            padding: "3px 6px", cursor: deletingId === entry.id ? "not-allowed" : "pointer",
                            color: C.muted, fontSize: 11, display: "flex", alignItems: "center",
                            opacity: deletingId === entry.id ? 0.5 : 1,
                          }}
                        >
                          {deletingId === entry.id ? (
                            <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 11, height: 11 }}>
                              <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4"/>
                              <path fill={C.muted} d="M4 12a8 8 0 018-8v8z"/>
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                            </svg>
                          )}
                        </button>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                          fill="none" stroke={isOpen ? C.purple : C.muted} strokeWidth="2"
                          style={{ transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
                          <path d="M6 9l6 6 6-6"/>
                        </svg>
                      </div>
                    </div>
 
                    {isOpen && (
                      <div style={{ padding: "12px 14px", borderTop: `1px solid ${C.purple}18`, display: "flex", flexDirection: "column", gap: 10 }}>
                        <div style={{ background: C.purple + "06", borderRadius: 10, border: `1px solid ${C.purple}18`, overflow: "hidden" }}>
                          <div style={{
                            padding: "7px 12px", borderBottom: `1px solid ${C.purple}14`,
                            background: C.purple + "0E", display: "flex", alignItems: "center", gap: 6,
                          }}>
                            <span style={{ fontSize: 12 }}>🎁</span>
                            <span style={{ fontSize: 10, fontWeight: 700, color: C.purple, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                              What the Data Shows
                            </span>
                          </div>
                          <div style={{ padding: "10px 12px" }}><MarkdownBlock text={entry.summary} /></div>
                        </div>
 
                        {entry.recommendations && (
                          <div style={{ background: C.teal + "06", borderRadius: 10, border: `1px solid ${C.teal}18`, overflow: "hidden" }}>
                            <div style={{
                              padding: "7px 12px", borderBottom: `1px solid ${C.teal}14`,
                              background: C.teal + "0E", display: "flex", alignItems: "center", gap: 6,
                            }}>
                              <span style={{ fontSize: 12 }}>🔮</span>
                              <span style={{ fontSize: 10, fontWeight: 700, color: C.teal, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                                Outlook & What to Anticipate
                              </span>
                            </div>
                            <div style={{ padding: "10px 12px" }}><MarkdownBlock text={entry.recommendations} /></div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

const LoyaltyAIFloatingButton = ({ onClick, hasNew }) => (
  <button
    onClick={onClick}
    title="Open Loyalty Analyst"
    style={{
      position: "fixed", bottom: 28, right: 28, width: 52, height: 52, borderRadius: "50%",
      background: C.purple, border: "none", cursor: "pointer",
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 4px 18px rgba(124,58,237,0.35)", zIndex: 998,
      transition: "transform 0.15s, box-shadow 0.15s",
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.08)"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(124,58,237,0.45)" }}
    onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 18px rgba(124,58,237,0.35)" }}
  >
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
      <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
    </svg>
    {hasNew && (
      <span style={{ position: "absolute", top: 2, right: 2, width: 10, height: 10, borderRadius: "50%", background: C.amber, border: "2px solid #fff" }} />
    )}
  </button>
)

export default function CustomerFeedback({ onGoToProfile }) {
    const [filterRating, setFilterRating] = useState('all')
    const [page, setPage] = useState(1)
    const [aiOpen, setAiOpen] = useState(false)
    const [aiSummary, setAiSummary] = useState(null)
    const [aiRecommendations, setAiRecommendations] = useState(null)
    const [aiLoading, setAiLoading] = useState(false)
    const [aiError, setAiError] = useState(null)
    const { history, loading: historyLoading, saveAnalysis, deleteAnalysis } = useCustomerFeedbackAIHistory()
    const { reviews, serviceRating, itemReview, loading, error: feedbackError, lastUpdated, refetch } = useCustomerFeedbackData()


    const navigate = useNavigate();

    

    const totalReviews = reviews.length
    const avgRating = totalReviews
        ? (reviews.reduce((s, r) => s + r.overall_rating, 0) / totalReviews).toFixed(1) : '-'

    const distribution = [5,4,3,2,1].map(stars => {
        const count = reviews.filter(r => r.overall_rating === stars).length
        return { stars, count, percentage: totalReviews ? Math.round((count / totalReviews) * 100) : 0}
    })

    const avgSpeed = serviceRating.filter(s => s.speed).length
        ? (serviceRating.filter(s => s.speed).reduce((s,r) => s + r.speed, 0) / 
            serviceRating.filter(s => s.speed).length).toFixed(1)
        : '-'
    
    const avgService = serviceRating.filter(s => s.service).length
        ? (serviceRating.filter(s => s.service).reduce((s, r) => s + r.service, 0) / 
            serviceRating.filter(s => s.service).length).toFixed(1)
        : '-'
    
    const avgItemRating = itemReview.length
        ? (itemReview.reduce((s, r) => s + r.rating, 0) / itemReview.length).toFixed(1)
        : '-'
    
    const allTags = [
        ...serviceRating.flatMap(s => {
            try {
                return [...JSON.parse(s.speed_tags || '[]'), ...JSON.parse(s.service_tags || '[]')]
            } 
            catch {
                return []
            }
        }),
        ...itemReview.flatMap(r => {
            try {
                return JSON.parse(r.tags || '[]')
            }
            catch {
                return []
            }
        }),
    ]

    const tagCounts = allTags.reduce((acc, t) => {
        acc[t] = (acc[t] || 0) + 1;
        return acc
    }, {})

    const topTags = Object.entries(tagCounts).sort((a, b) => b[1] - a[1]).slice(0, 8)
    
    const sentiment = parseFloat(avgRating) >= 4.5 ? 'Positive' : parseFloat(avgRating) >= 3 ? 'Neutral' : 'Negative'
    const sentimentColor = sentiment === 'Positive' ? 'text-purple-600' : sentiment === 'Neutral' ? 'text-amber-600' : 'text-red-600'

    const fiveStarCount = distribution.find(d => d.stars === 5)?.count ?? 0
    const fiveStarPct = distribution.find(d => d.stars === 5)?.percentage ?? 0

    let displayed = [...reviews]
    if (filterRating !== 'all') displayed = displayed.filter(r => r.overall_rating === parseInt(filterRating))
    const totalPages = Math.ceil(displayed.length / REVIEW_PER_PAGE)
    const paginated = displayed.slice((page - 1) * REVIEW_PER_PAGE, page * REVIEW_PER_PAGE)

    const handleGenerateAnalysis = async () => {
      setAiLoading(true)
      setAiError(null)
      try {
          const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
          const res = await fetch(`${API_BASE_URL}/api/feedback/summarize`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...authHeaders() },
              body: JSON.stringify({}),
          })
          if (!res.ok) throw new Error('Failed to generate analysis')
          const result = await res.json()
          setAiSummary(result.summary)
          setAiRecommendations(result.recommendations)
          const { data, error } = await saveAnalysis({ summary: result.summary, recommendations: result.recommendations })
            if (error) {
                console.error('Failed to save feedback AI history:', error)
                setAiError(prev => prev ?? `Analysis generated but not saved: ${error.message}`)
            }
      } catch (err) {
          setAiError(err.message || 'Failed to generate analysis. Please try again.')
      } finally {
          setAiLoading(false)
      }
    }
    return (
        <div className="flex-1 overflow-y-auto bg-gray-50">
            {/* Header */}
             <Header 
                title="Customer Feedback & Reviews" 
                subtitle="AI-moderated customer feedback and sentiment analysis" 
                 onGoToProfile={onGoToProfile}
            />

            <div className="p-4 sm:p-6 lg:p-8">
 
         {loading ? (
          <div className="flex items-center justify-center py-24 text-gray-400">
            <svg className="animate-spin mr-3 w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            Loading feedback…
          </div>
        ) : feedbackError ? (
          <div className="flex flex-col items-center justify-center py-24 text-center gap-3">
            <p className="text-red-600 font-medium">Failed to load feedback data</p>
            <p className="text-sm text-gray-500">{feedbackError}</p>
            <button
              onClick={refetch}
              className="px-4 py-2 bg-[#A0826D] text-white rounded-lg text-sm font-medium hover:bg-[#8a6f5c]"
            >
              Retry
            </button>
          </div>
          ) : (
          <>
            {/* Stats Cards  */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">              <StatCard label="Avg Rating" value={avgRating} sub={`Based on ${totalReviews} reviews`} color="text-green-600" />
              <StatCard label="5-Star Reviews" value={`${fiveStarPct}%`} sub={`${fiveStarCount} reviews`} color="text-blue-600" />
              <StatCard
                label="Avg Speed / Service"
                value={`${avgSpeed} / ${avgService}`}
                sub={`Item avg: ${avgItemRating} ★`}
                color="text-yellow-600"
              />
              <StatCard label="Sentiment" value={sentiment} sub="Derived from ratings" color={sentimentColor} />
            </div>
 
            {/* Rating Distribution  */}
            <div className={`${CARD} p-6 mb-8`}>
              <h3 className="text-lg font-semibold text-gray-800 mb-6">Rating Distribution</h3>
              <div className="space-y-4">
                {distribution.map(({ stars, count, percentage }) => (
                  <div key={stars} className="flex items-center gap-2 sm:gap-4">
                    <div className="flex items-center gap-1 w-12 sm:w-20 flex-shrink-0">
                      <span className="text-sm font-medium text-gray-700">{stars}</span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" className="text-yellow-400">
                        <path fill="currentColor" d="m12 17.27l4.15 2.51c.76.46 1.69-.22 1.49-1.08l-1.1-4.72l3.67-3.18c.67-.58.31-1.68-.57-1.75l-4.83-.41l-1.89-4.46c-.34-.81-1.5-.81-1.84 0L9.19 8.63l-4.83.41c-.88.07-1.24 1.17-.57 1.75l3.67 3.18l-1.1 4.72c-.2.86.73 1.54 1.49 1.08z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-yellow-400 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                    <div className="w-16 sm:w-24 text-right flex-shrink-0">
                      <span className="text-xs sm:text-sm text-gray-600">{count} <span className="hidden sm:inline">reviews</span></span>
                    </div>
                    <div className="w-12 sm:w-16 text-right flex-shrink-0">
                      <span className="text-xs sm:text-sm font-medium text-gray-700">{percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
 
            {/* Top Tags */}
            {topTags.length > 0 && (
              <div className={`${CARD} p-6 mb-8`}>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Feedback Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {topTags.map(([tag, count]) => (
                    <span key={tag} className="px-3 py-1 bg-[#F5EFE6] text-[#A0826D] text-sm font-medium rounded-full">
                      {tag} <span className="text-xs opacity-70">× {count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
 
            {/* Reviews Table */}
            <div className={`${CARD}`}>
              <div className="p-4 sm:p-6 border-b border-gray-200">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">Recent Reviews</h3>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                    <select
                      value={filterRating}
                      onChange={e => { setFilterRating(e.target.value); setPage(1) }}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#A0826D] w-full sm:w-auto"
                    >
                      <option value="all">All Ratings</option>
                      {['5','4','3','2','1'].map(v => (
                        <option key={v} value={v}>{v} Stars</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
 
              <div className="divide-y divide-gray-200">
                {paginated.length === 0 ? (
                  <p className="p-8 text-center text-gray-400">No reviews found.</p>
                ) : paginated.map(review => (
                  <ReviewCard key={review.review_id} review={review} />
                ))}
              </div>
 
              <Pagination
                page={page}
                totalItems={displayed.length}
                pageSize={REVIEW_PER_PAGE}
                onPageChange={setPage}
              />
            </div>
          </>
        )}
      </div>
      <LoyaltyAIFloatingButton onClick={() => setAiOpen(true)} hasNew={false} />
      <FeedbackAISidebar
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          summary={aiSummary}
          recommendations={aiRecommendations}
          loading={aiLoading}
          error={aiError}
          onGenerate={handleGenerateAnalysis}
          hasData={totalReviews > 0}
          history={history}
          historyLoading={historyLoading}
          onDeleteHistory={deleteAnalysis}
      />
    </div>
  )
}
 

function StatCard({ label, value, sub, color }) {
  return (
    <div className={`${CARD} p-6`}>
      <p className="text-sm text-gray-600 mb-2">{label}</p>
      <p className={`text-3xl font-bold ${color}`}>{value}</p>
      <p className="text-sm text-gray-500 mt-2">{sub}</p>
    </div>
  )
}
 
function ReviewCard({ review }) {
  const { user_id, overall_rating, review_text, loyalty_points_awarded, created_at, reviewer_name } = review
  const initials = reviewer_name && reviewer_name !== 'Unknown'
    ? reviewer_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : '??'

  return (
    <div className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
            <div className="flex items-center gap-3 min-w-0">
                <div className={`w-10 h-10 flex-shrink-0 ${avatarColor(user_id)} rounded-full flex items-center justify-center text-white font-semibold text-sm`}>
                    {initials}
                </div>
            <div className="min-w-0">
                <p className="font-medium text-gray-800 text-sm truncate">{reviewer_name}</p> 
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <StarRow rating={overall_rating} />
                <span className="text-xs text-gray-500">{timeAgo(created_at)}</span>
                </div>
            </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap sm:flex-shrink-0">
                {loyalty_points_awarded && (
                    <span className="px-3 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full whitespace-nowrap">
                    Points Awarded
                    </span>
                )}
                <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full whitespace-nowrap">
                    Verified Purchase
                </span>
            </div>
      </div>
      
      {review_text ? (
        <p className="text-gray-700 text-sm mb-3">{review_text}</p>
      ) : (
        <p className="text-gray-400 text-sm italic mb-3">No written review.</p>
      )}
    </div>
  )
}
 
function PageBtn({ label, onClick, disabled, active }) {
  if (active) {
    return (
      <button onClick={onClick} className="px-4 py-2 bg-[#A0826D] text-white rounded-lg text-sm font-medium">
        {label}
      </button>
    )
  }
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {label}
    </button>
  )
}
 